import * as React from "react";

import { cn } from "@/lib/utils";

const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<"textarea">
>(({ className, onChange, ...props }, ref) => {
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

  const handleResize = React.useCallback((element: HTMLTextAreaElement) => {
    // Reset height to auto to get the correct scrollHeight
    element.style.height = "auto";
    // Set the height to scrollHeight to fit content
    const newHeight = Math.min(element.scrollHeight, 120); // 120px is roughly 5 lines
    element.style.height = `${newHeight}px`;
  }, []);

  React.useEffect(() => {
    if (textareaRef.current) {
      handleResize(textareaRef.current);
    }
  }, [handleResize]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleResize(e.target);
    onChange?.(e);
  };

  return (
    <textarea
      className={cn(
        "flex min-h-[44px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className,
      )}
      ref={(element) => {
        // Handle both refs
        textareaRef.current = element;
        if (typeof ref === "function") {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      }}
      onChange={handleChange}
      {...props}
    />
  );
});
Textarea.displayName = "Textarea";

export { Textarea };
